import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle, ArrowLeft, Trophy, Flag, Share, Check, Trash, PencilSimple, X } from "@phosphor-icons/react";
import ShareRoundCard from "../components/ShareRoundCard";
import ShotMapCard from "../components/ShotMapCard";
import ReviewNudgeModal, { shouldShowReviewNudge } from "../components/ReviewNudgeModal";

export default function RoundSummary() {
  const { id } = useParams();
  const { user } = useAuth();
  const isPro = user?.role === "admin" || ["active", "trialing"].includes(user?.subscription_status);
  const nav = useNavigate();
  const [round, setRound] = useState(null);
  const [course, setCourse] = useState(null);
  const [league, setLeague] = useState(null);
  const [shared, setShared] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Live scoring summary (Stableford / Match play). Null for strokeplay.
  const [scoring, setScoring] = useState(null);
  // Per-hole inline edit — lets the user fix scores when the app crashed
  // mid-round and auto-par filled the wrong number.
  const [editingHole, setEditingHole] = useState(null);
  const [editStrokes, setEditStrokes] = useState("");
  const [editPutts, setEditPutts] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Show the "rate / give feedback" deflector after the 5th completed round.
  // Decoupled from `round` load so it only fires for new-completion screens.
  const [showReviewNudge, setShowReviewNudge] = useState(false);

  const startEdit = (hole, existingScore) => {
    setEditingHole(hole.number);
    setEditStrokes(String(existingScore?.strokes ?? hole.par));
    setEditPutts(existingScore?.putts != null ? String(existingScore.putts) : "");
  };

  const cancelEdit = () => {
    setEditingHole(null);
    setEditStrokes("");
    setEditPutts("");
  };

  const saveEdit = async (hole) => {
    const strokes = Math.max(1, Math.min(15, Number(editStrokes) || hole.par));
    const puttsRaw = editPutts === "" ? null : Math.max(0, Math.min(10, Number(editPutts) || 0));
    setSavingEdit(true);
    try {
      const { data } = await api.post(`/rounds/${id}/score`, {
        hole_number: hole.number,
        strokes,
        putts: puttsRaw,
      });
      setRound(data);
      cancelEdit();
    } catch (err) {
      console.warn("RoundSummary: edit hole save failed", err);
      window.alert("Couldn't save that change. Try again.");
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data: r } = await api.get(`/rounds/${id}`);
      setRound(r);
      const { data: c } = await api.get(`/courses/${r.course_id}`);
      setCourse(c);
      if (r.league_id) {
        try {
          const { data: l } = await api.get(`/leagues/${r.league_id}`);
          setLeague(l.league);
        } catch (err) {
          console.warn("RoundSummary: failed to fetch league context", err);
        }
      }
      // Stableford / match-play scoring summary for the round-end display.
      if (r.scoring_mode && r.scoring_mode !== "strokeplay") {
        try {
          const { data: sc } = await api.get(`/rounds/${id}/scoring`);
          setScoring(sc);
        } catch (err) {
          console.warn("RoundSummary: failed to fetch derived scoring", err);
        }
      }
      // Review-nudge trigger: count completed rounds and pop the
      // "loving it / hit a snag" modal after the 5th. Only when the round
      // we're viewing is actually finished (not a partial save).
      if (r.completed) {
        try {
          const { data: rounds } = await api.get("/rounds");
          const completedCount = (rounds || []).filter((x) => x.completed).length;
          if (shouldShowReviewNudge(completedCount)) {
            // Small delay so the user sees their score first.
            setTimeout(() => setShowReviewNudge(true), 1800);
          }
        } catch (err) {
          console.warn("RoundSummary: failed to count rounds for review nudge", err);
        }
      }
    })();
  }, [id]);

  if (!round || !course) return <div className="px-5 pt-6">Loading...</div>;
  const vsPar = round.total_strokes - round.total_par;
  const isFourball = Array.isArray(round.players) && round.players.length > 1;

  const shareWithMates = async () => {
    if (!league) return;
    const url = `${window.location.origin}/join/${league.code}`;
    const vsParText = vsPar === 0 ? "level par" : vsPar > 0 ? `+${vsPar}` : `${vsPar}`;
    const shareData = {
      title: `Beat my round on Fairway`,
      text: `Just shot ${round.total_strokes} (${vsParText}) at ${course.name}. Think you can do better? Join my league "${league.name}".`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      // User cancelled the share sheet, or Web Share API failed — fall through
      // to clipboard copy.
      if (err && err.name !== "AbortError") {
        console.warn("RoundSummary: native share failed", err);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch (_err) {
      /* clipboard not available */
    }
  };

  return (
    <div className="px-5 pt-6 pb-8">
      <Link to="/app" className="btn-ghost -ml-2 mb-3" data-testid="summary-back-link">
        <ArrowLeft size={20} weight="bold" /> Done
      </Link>

      <div className="flex justify-center mb-3">
        <div className="h-16 w-16 rounded-full bg-[var(--accent-turf)]/20 flex items-center justify-center text-[var(--status-good)]">
          <CheckCircle size={48} weight="fill" />
        </div>
      </div>
      <h1 className="text-center font-heading font-black tracking-tighter-2 text-3xl text-[var(--primary)]">Round complete</h1>
      <p className="text-center text-[var(--text-secondary)] mb-6">{course.name}</p>

      <div className="surface-card p-6 mb-5 text-center">
        <p className="label-uppercase">{isFourball ? "Your score" : "Final score"}</p>
        <div className="font-mono-num font-black text-6xl text-[var(--primary)] my-1" data-testid="summary-total-strokes">{round.total_strokes}</div>
        <p className="font-heading font-bold text-lg" data-testid="summary-vs-par">
          {vsPar === 0 ? "Level par" : vsPar > 0 ? `+${vsPar} over par` : `${vsPar} under par`}
        </p>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Course par {round.total_par} · {round.scores.length} holes played</p>
      </div>

      {/* WHS handicap update — only surfaces when the round was
          handicap-eligible (course has CR/slope, all 18 holes scored). */}
      {round.handicap_eligible && round.score_differential != null && (
        <div
          className="surface-card p-6 mb-5 text-center border border-green-500/20"
          data-testid="summary-handicap-card"
        >
          <p className="label-uppercase text-green-300">WHS Score Differential</p>
          <div className="font-mono-num font-black text-6xl text-[var(--primary)] my-1" data-testid="summary-score-differential">
            {round.score_differential > 0 ? "+" : ""}{round.score_differential.toFixed(1)}
          </div>
          <p className="font-heading font-bold text-sm">
            Adjusted Gross {round.adjusted_gross}
            {round.applied_course_handicap != null && (
              <span className="opacity-70"> · Course Handicap {round.applied_course_handicap}</span>
            )}
          </p>
          <p className="text-xs text-[var(--text-secondary)] italic mt-2">
            This round now counts toward your Handicap Index. See your profile for the running average.
          </p>
        </div>
      )}

      {/* Stableford / Match-play result card — only shows for non-strokeplay
          rounds and once the live scoring fetch has replied. */}
      {scoring && scoring.mode === "stableford" && (() => {
        const me = (scoring.per_player || []).find((p) => p.player_index === 0) || (scoring.per_player || [])[0];
        if (!me) return null;
        return (
          <div className="surface-card p-6 mb-5 text-center" data-testid="summary-stableford-card">
            <p className="label-uppercase">
              Stableford {scoring.net ? "· Net" : "· Gross"}
              {scoring.net && me.applied_handicap != null ? ` · hcp ${Math.round(me.applied_handicap)}` : ""}
            </p>
            <div className="font-mono-num font-black text-6xl text-[var(--primary)] my-1" data-testid="summary-stableford-points">
              {me.points}
            </div>
            <p className="font-heading font-bold text-lg">points</p>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Birdie 3 · Par 2 · Bogey 1 · Double+ 0 (per hole)
            </p>
          </div>
        );
      })()}

      {scoring && scoring.mode === "match_play" && scoring.status && (() => {
        const s = scoring.status;
        const winning = (s.diff || 0) > 0;
        const losing = (s.diff || 0) < 0;
        const bg = winning ? "var(--primary)" : losing ? "var(--accent-terracotta)" : "var(--text)";
        return (
          <div
            className="rounded-2xl p-6 mb-5 text-center shadow-lg"
            style={{ background: bg, color: "#F2EEE7" }}
            data-testid="summary-match-card"
          >
            <p className="text-[10px] uppercase tracking-[3px] font-bold opacity-80">Match play</p>
            <div
              className="font-heading font-black tracking-tighter-2 text-4xl my-2"
              data-testid="summary-match-label"
            >
              {s.label}
            </div>
            <p className="text-sm opacity-90">
              {(scoring.team_a_names || []).join(" & ") || "Team A"}
              {" vs "}
              {(scoring.team_b_names || []).join(" & ") || "Team B"}
            </p>
          </div>
        );
      })()}

      {/* GPS shot tracker recap — per-club distances + hole-by-hole shots.
          Only renders if the user logged shots during the round. */}
      {Array.isArray(round.shots) && round.shots.length > 0 && (
        <ShotMapCard shots={round.shots} />
      )}

      {/* Shareable recap card — works for both solo and fourball.
          Free users in a fourball see a Pro upsell instead of the recap. */}
      <div className="mb-5">
        {(() => {
          const isFourballEarly = Array.isArray(round.players) && round.players.length > 1;
          if (isFourballEarly && !isPro) {
            return (
              <Link
                to="/subscribe"
                data-testid="fourball-share-pro-gate"
                className="block rounded-2xl border-2 border-dashed border-[var(--primary)] bg-[var(--accent-sand)] p-5 text-center hover:bg-[var(--accent-sand)]/70 transition"
              >
                <div className="text-2xl mb-1">✨</div>
                <div className="font-heading font-bold text-[var(--primary)] mb-1">
                  Share this fourball with the lads?
                </div>
                <p className="text-xs text-[var(--text)] mb-2">
                  Branded shareable recap cards are part of Fairway Pro. Lock in bragging rights with one tap.
                </p>
                <span className="inline-block text-xs font-bold uppercase tracking-wider text-[var(--primary)] underline">
                  Go Pro →
                </span>
              </Link>
            );
          }
          return <ShareRoundCard round={round} course={course} />;
        })()}
      </div>

      {isFourball && (() => {
        // Lowest total wins among players who actually played at least one hole.
        const ranked = [...round.players]
          .filter((p) => p.scores.length > 0)
          .sort((a, b) => a.total_strokes - b.total_strokes);
        const winner = ranked[0];
        const tied = winner ? ranked.filter((p) => p.total_strokes === winner.total_strokes) : [];
        if (!winner) return null;
        return (
          <div
            className="surface-card-flat p-4 mb-5 flex items-center gap-3"
            style={{ background: "var(--accent-sand)" }}
            data-testid="fourball-winner-banner"
          >
            <Trophy size={26} weight="fill" className="text-[var(--accent-terracotta)] shrink-0" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-[var(--text-secondary)] font-heading">
                {tied.length > 1 ? "Tied at the top" : "Match winner"}
              </div>
              <div className="font-heading font-bold text-[var(--primary)]" data-testid="fourball-winner-name">
                {tied.map((p) => p.name).join(" & ")} · <span className="font-mono-num">{winner.total_strokes}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {round.league_id && league && (
        <div className="surface-card-flat p-4 mb-5 bg-[var(--accent-sand)]" data-testid="round-league-share-block">
          <div className="flex items-center gap-3 mb-3">
            <Trophy size={22} weight="fill" className="text-[var(--accent-terracotta)]" />
            <div className="flex-1">
              <div className="font-heading font-bold text-[var(--primary)]">Posted to {league.name}</div>
              <div className="text-xs text-[var(--text-secondary)]">Your score is on the leaderboard.</div>
            </div>
            <Link to={`/app/leagues/${league.id}`} data-testid="view-league-from-summary" className="text-sm font-semibold text-[var(--primary)] underline">View</Link>
          </div>
          <button onClick={shareWithMates} className="btn-primary w-full" data-testid="share-with-mates-button">
            {shared ? <><Check size={18} weight="bold" /> Link copied!</> : <><Share size={18} weight="fill" /> Challenge mates to beat it</>}
          </button>
        </div>
      )}

      {round.tee_color && (
        <div
          className="text-xs text-[var(--text-secondary)] mb-3 -mt-2 flex items-center gap-1.5"
          data-testid="round-tee-color"
        >
          Played from
          <span className="inline-flex items-center gap-1 font-bold text-[var(--text)] capitalize">
            <span
              aria-hidden="true"
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{
                background: {
                  white: "#fff", yellow: "#FFE45E", blue: "#3B82F6", red: "#ef4444",
                  black: "#111", gold: "#D4AF37", green: "#16a34a",
                }[round.tee_color] || "transparent",
                border: round.tee_color === "white" ? "1px solid var(--border)" : "none",
              }}
            />
            {round.tee_color}
          </span>
          tees
        </div>
      )}

      <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-3">Hole-by-hole</h3>
      {isFourball ? (
        <div className="surface-card-flat overflow-hidden" data-testid="fourball-scorecard">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-full">
              <thead className="bg-[var(--secondary)] text-[var(--primary)]">
                <tr>
                  <th className="py-2 px-2 text-left font-heading">Hole</th>
                  <th className="py-2 px-2 font-heading text-center">Par</th>
                  {round.players.map((p) => (
                    <th
                      key={p.player_index}
                      className="py-2 px-2 font-heading text-center max-w-[80px]"
                      data-testid={`scorecard-player-col-${p.player_index}`}
                    >
                      <div className="truncate">{p.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {course.holes.map((h) => (
                  <tr key={h.number} className="border-t border-[var(--border-default)]">
                    <td className="py-2 px-2 font-mono-num font-bold">{h.number}</td>
                    <td className="py-2 px-2 font-mono-num text-center text-[var(--text-secondary)]">{h.par}</td>
                    {round.players.map((p) => {
                      const s = p.scores.find((x) => x.hole_number === h.number);
                      const diff = s ? s.strokes - h.par : null;
                      return (
                        <td
                          key={p.player_index}
                          className={`py-2 px-2 font-mono-num text-center ${diffClass(diff)}`}
                          data-testid={`scorecard-cell-p${p.player_index}-h${h.number}`}
                        >
                          {s?.strokes ?? "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t-2 border-[var(--primary)] bg-[var(--bg-app)]">
                  <td className="py-3 px-2 font-heading font-bold text-[var(--primary)]">Total</td>
                  <td className="py-3 px-2 font-mono-num font-bold text-center text-[var(--primary)]">{round.total_par}</td>
                  {round.players.map((p) => (
                    <td
                      key={p.player_index}
                      className="py-3 px-2 font-mono-num font-black text-center text-[var(--primary)]"
                      data-testid={`scorecard-total-p${p.player_index}`}
                    >
                      {p.total_strokes}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="surface-card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--secondary)] text-[var(--primary)]">
              <tr>
                <th className="py-2 px-2 text-left font-heading">Hole</th>
                <th className="py-2 px-2 font-heading text-center">Par</th>
                <th className="py-2 px-2 font-heading text-center">
                  Tee<span className="opacity-50">→</span>green
                  <span className="block text-[9px] font-normal opacity-60 normal-case tracking-normal">no putts</span>
                </th>
                <th className="py-2 px-2 font-heading text-center">Putts</th>
                <th className="py-2 px-2 font-heading text-center">Score</th>
                <th className="py-2 px-2 text-right font-heading">±</th>
                <th className="py-2 px-2 font-heading text-center w-10"></th>
              </tr>
            </thead>
            <tbody>
              {course.holes.map((h) => {
                const s = round.scores.find((x) => x.hole_number === h.number);
                const diff = s ? s.strokes - h.par : null;
                // Strokes from tee = total score minus putts. Pure derived value;
                // we keep total `strokes` as the canonical R&A-compliant score.
                const teeToGreen = s && s.strokes != null && s.putts != null
                  ? Math.max(0, s.strokes - s.putts)
                  : null;
                const isEditing = editingHole === h.number;
                return (
                  <tr key={h.number} className="border-t border-[var(--border-default)]" data-testid={`score-row-${h.number}`}>
                    <td className="py-2 px-2 font-mono-num font-bold">{h.number}</td>
                    <td className="py-2 px-2 font-mono-num text-center">{h.par}</td>
                    <td
                      className="py-2 px-2 font-mono-num text-center text-[var(--text-secondary)]"
                      data-testid={`tee-to-green-${h.number}`}
                    >
                      {teeToGreen ?? "—"}
                    </td>
                    <td className="py-2 px-2 font-mono-num text-center text-[var(--text-secondary)]">
                      {isEditing ? (
                        <input
                          type="number"
                          min={0}
                          max={10}
                          value={editPutts}
                          onChange={(e) => setEditPutts(e.target.value)}
                          data-testid={`edit-putts-${h.number}`}
                          className="w-12 text-center px-1 py-1 rounded border border-[var(--border)] font-mono-num"
                        />
                      ) : (
                        s?.putts ?? "—"
                      )}
                    </td>
                    <td className="py-2 px-2 font-mono-num font-bold text-center">
                      {isEditing ? (
                        <input
                          type="number"
                          min={1}
                          max={15}
                          value={editStrokes}
                          onChange={(e) => setEditStrokes(e.target.value)}
                          data-testid={`edit-strokes-${h.number}`}
                          className="w-12 text-center px-1 py-1 rounded border border-[var(--border)] font-mono-num font-bold"
                        />
                      ) : (
                        s?.strokes ?? "—"
                      )}
                    </td>
                    <td className={`py-2 px-2 font-mono-num text-right ${diffClass(diff)}`}>{diff === null ? "—" : (diff === 0 ? "E" : diff > 0 ? `+${diff}` : diff)}</td>
                    <td className="py-2 px-2 text-center">
                      {isEditing ? (
                        <div className="flex gap-1 justify-center">
                          <button
                            type="button"
                            onClick={() => saveEdit(h)}
                            disabled={savingEdit}
                            data-testid={`save-edit-${h.number}`}
                            aria-label="Save"
                            className="text-green-700 hover:bg-green-500/10 rounded p-1"
                          >
                            <Check size={14} weight="bold" />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            data-testid={`cancel-edit-${h.number}`}
                            aria-label="Cancel"
                            className="text-[var(--text-secondary)] hover:bg-[var(--bg-app)] rounded p-1"
                          >
                            <X size={14} weight="bold" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(h, s)}
                          data-testid={`edit-row-${h.number}`}
                          aria-label={`Edit hole ${h.number}`}
                          className="text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-app)] rounded p-1"
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-[var(--primary)] bg-[var(--bg-app)]">
                <td className="py-2 px-2 font-heading font-bold text-[var(--primary)]">Total</td>
                <td className="py-2 px-2 font-mono-num font-bold text-center text-[var(--primary)]">{round.total_par}</td>
                {(() => {
                  const totalPutts = round.scores.reduce((acc, x) => acc + (x.putts || 0), 0);
                  const totalTeeToGreen = Math.max(0, round.total_strokes - totalPutts);
                  return (
                    <>
                      <td className="py-2 px-2 font-mono-num font-bold text-center text-[var(--primary)]">
                        {totalTeeToGreen || "—"}
                      </td>
                      <td className="py-2 px-2 font-mono-num font-bold text-center text-[var(--primary)]">
                        {totalPutts || "—"}
                      </td>
                    </>
                  );
                })()}
                <td className="py-2 px-2 font-mono-num font-black text-center text-[var(--primary)]">{round.total_strokes}</td>
                <td className="py-2 px-2 font-mono-num font-bold text-right text-[var(--primary)]">
                  {(() => { const vp = round.total_strokes - round.total_par; return vp === 0 ? "E" : vp > 0 ? `+${vp}` : vp; })()}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <p className="px-3 py-2 text-[10px] text-[var(--text-secondary)] italic border-t border-[var(--border-default)] bg-[var(--bg-app)]">
            <strong>Tee→green</strong> = strokes before the green · <strong>Putts</strong> = strokes on the green · <strong>Score</strong> = the two added together (this is what counts for handicap).
          </p>
        </div>
      )}

      <Link to="/app/courses" className="btn-primary w-full mt-6" data-testid="play-another-button">
        <Flag size={20} weight="fill" /> Play another round
      </Link>

      <button
        type="button"
        disabled={deleting}
        onClick={async () => {
          if (!window.confirm(`Delete this round? It will also be removed from any league standings.`)) return;
          setDeleting(true);
          try {
            await api.delete(`/rounds/${id}`);
            nav("/app/stats", { replace: true });
          } catch (err) {
            console.warn("RoundSummary: delete round failed", err);
            window.alert("Couldn't delete that round. Try again.");
            setDeleting(false);
          }
        }}
        data-testid="round-summary-delete"
        className="w-full mt-3 text-xs font-bold uppercase tracking-wider text-red-600 hover:underline py-2 flex items-center justify-center gap-1.5"
      >
        <Trash size={14} weight="bold" /> {deleting ? "Deleting…" : "Delete this round"}
      </button>

      {showReviewNudge && (
        <ReviewNudgeModal onClose={() => setShowReviewNudge(false)} />
      )}
    </div>
  );
}

function diffClass(d) {
  if (d === null) return "text-[var(--text-secondary)]";
  if (d <= -1) return "score-birdie font-bold";
  if (d === 0) return "score-par font-bold";
  if (d === 1) return "score-bogey font-bold";
  return "score-double font-bold";
}
